import { lazy, Suspense, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { RedirectHandler } from "@/components/RedirectHandler";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Index from "./pages/Index.tsx";
import ResourcePage from "./pages/ResourcePage.tsx";
import FAQsPage from "@/pages/FAQsPage";

declare global {
  interface Window {
    __REACT_QUERY_DATA__?: Record<string, unknown>;
    __QUERY_CLIENT__?: QueryClient;
  }
}

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const DynamicPage = lazy(() => import("./pages/DynamicPage.tsx"));
const UseCasesPage = lazy(() => import("./pages/UseCasesPage.tsx"));
const IndustriesPage = lazy(() => import("./pages/IndustriesPage.tsx"));
const CustomersPage = lazy(() => import("./pages/CustomersPage.tsx"));
const PricingPage = lazy(() => import("./pages/PricingPage.tsx"));
const BlogPage = lazy(() => import("./pages/BlogPage.tsx"));
const BlogDetailPage = lazy(() => import("./pages/BlogDetailPage.tsx"));
const NewsroomPage = lazy(() => import("./pages/NewsroomPage.tsx"));
const Toaster = lazy(() => import("@/components/ui/toaster").then((module) => ({ default: module.Toaster })));
const SonnerToaster = lazy(() => import("@/components/ui/sonner").then((module) => ({ default: module.Toaster })));

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 5 * 60 * 1000 } },
});

// Only set during the prerender build (npm run build:prerender). Lets
// prerender.mjs read the cache after data has loaded, so it can dump it
// into the HTML for the seeding block below to pick up on real visits.
if (typeof window !== "undefined" && import.meta.env.MODE === "prerender") {
  window.__QUERY_CLIENT__ = queryClient;
}

// Seed the cache from prerendered data (injected by prerender.mjs into <head>)
// so hydration reuses it instead of showing a skeleton and refetching.
// Runs once at module load, before any component mounts.
if (typeof window !== "undefined" && window.__REACT_QUERY_DATA__) {
  for (const [key, value] of Object.entries(window.__REACT_QUERY_DATA__)) {
    queryClient.setQueryData(JSON.parse(key), value);
  }
  delete window.__REACT_QUERY_DATA__;
}

const RouteFallback = () => (
  <div className="min-h-screen bg-background" aria-hidden="true">
    {/*
      Keep the Navbar/Footer chrome visually stable across the lazy-loaded
      page-chunk swap. Previously this was a bare blank div, so when the
      real page (which also renders Navbar/Footer) popped in, the header
      and footer appeared from nothing — a full-viewport layout shift on
      every route change. Rendering them here too means nothing moves
      when the real content replaces this fallback.
    */}
    <Navbar />
    <div style={{ height: 76 }} aria-hidden="true" />
  </div>
);

const App = () => {
  const [showDeferredUi, setShowDeferredUi] = useState(false);

  useEffect(() => {
    const schedule = window.requestIdleCallback ?? ((callback: IdleRequestCallback) => window.setTimeout(callback, 1));
    const cancel = window.cancelIdleCallback ?? window.clearTimeout;
    const handle = schedule(() => setShowDeferredUi(true));

    return () => cancel(handle);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        {showDeferredUi ? (
          <Suspense fallback={null}>
            <Toaster />
            <SonnerToaster />
          </Suspense>
        ) : null}
        <ScrollToTop />
        <RedirectHandler enabled={true} />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/customers" element={<CustomersPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/solutions/use-cases" element={<UseCasesPage />} />
            <Route path="/solutions/industries" element={<IndustriesPage />} />
            <Route path="/resources/blog" element={<BlogPage />} />
            <Route path="/resources/blog/:documentId" element={<BlogDetailPage />} />
            <Route path="/resources/:slug" element={<ResourcePage />} />
            <Route path="/resources/newsroom" element={<NewsroomPage />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            {/* Any page a client creates in Strapi (any slug) renders here automatically. */}
            <Route path="/:slug" element={<DynamicPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;